import {
  AccordionGroup,
  Box,
  HStack,
  MixpanelEvent,
  Stack,
  Tab,
  TabList,
  TabPanels,
  TabSize,
  Tabs,
  Text,
  tabLabelerEnumerated,
  useMixpanel,
  usePersistedTabs,
  usePortal,
  useTabs,
  type PersistedDefaultTabProps,
  type TabProps,
} from '@talos/kyoko';
import { useCallback } from 'react';
import { useBlotterTableTabCloning } from '../../../providers/AppConfigRedux/useBlotterTableTabCloning';
import { CareOrderActionButtons } from './CareOrderActionButtons';
import { CareOrderBlotter } from './CareOrderBlotter';
import { CARE_ORDER_BLOTTER_PORTAL_ID, CARE_ORDER_BLOTTER_PREFIX } from './types';

interface CareOrdersWrapperProps {
  panelId: string;
}

interface CareOrdersTabProps extends TabProps {
  showAllDecimals?: boolean;
  expandAllGroups?: boolean;
  includeOpenCareOrdersOutsideDateRange?: boolean;
}

const tabLabeler = tabLabelerEnumerated('Care Orders');
const ALL_CARE_ORDERS_TAB_ID = 'all-care-orders';

const defaultTabs: PersistedDefaultTabProps<CareOrdersTabProps>[] = [
  {
    id: ALL_CARE_ORDERS_TAB_ID,
    label: 'All',
    editable: true,
    closable: false, // the tab is excluded from persistence, so cannot be removed
  },
];
const excludedIDs = defaultTabs.map(t => t.id);

export const CareOrdersWrapper = ({ panelId }: CareOrdersWrapperProps) => {
  const mixpanel = useMixpanel();
  const { setPortalRef: filtersContainerRef } = usePortal(CARE_ORDER_BLOTTER_PORTAL_ID);
  const getBlotterID = useCallback((tabID: string) => `${CARE_ORDER_BLOTTER_PREFIX}/${panelId}/${tabID}`, [panelId]);

  const handleAdd = useCallback(() => {
    mixpanel.track(MixpanelEvent.AddTab);
  }, [mixpanel]);

  const persistedTabs = usePersistedTabs<CareOrdersTabProps>(`${CARE_ORDER_BLOTTER_PREFIX}/${panelId}`, {
    defaultInitialItems: defaultTabs,
    defaultInitialSelectedIndex: 0,
    excludedIDs,
  });

  const tabs = useTabs<CareOrdersTabProps>({
    ...persistedTabs,
    showAddTab: true,
    tabLabeler,
    onAdd: handleAdd,
    onClone: useBlotterTableTabCloning({ getBlotterID }),
  });
  const handleCloneTab = useCallback(() => {
    tabs.clone(tabs.selectedIndex);
  }, [tabs]);

  return (
    <Stack h="100%">
      <HStack
        justifyContent="space-between"
        px="spacing12"
        minHeight="40px"
        borderBottom="1px solid var(--backgroundBody)"
        background="backgroundContent"
      >
        <Text color="colorTextImportant" fontWeight={500}>
          Care Orders
        </Text>
        <CareOrderActionButtons />
      </HStack>
      <Tabs {...tabs} h="100%" size={TabSize.Large}>
        <TabList rightItems={<Box ref={filtersContainerRef} />} background="backgroundContent" minHeight="40px">
          {tabs.items.map((tab, idx) => (
            <Tab key={idx} {...tab} reorderable={true} />
          ))}
        </TabList>
        <TabPanels flex={1} display="flex" flexDirection="column">
          {tabs.items.map(tab => (
            <AccordionGroup key={tab.id}>
              <CareOrderBlotter
                blotterID={`${CARE_ORDER_BLOTTER_PREFIX}/${panelId}/${tab.id}`}
                portalId={CARE_ORDER_BLOTTER_PORTAL_ID}
                showHUD={true}
                tabData={{
                  showAllDecimals: tab.showAllDecimals,
                  expandAllGroups: tab.expandAllGroups,
                  includeOpenCareOrdersOutsideDateRange: tab.includeOpenCareOrdersOutsideDateRange ?? true,
                }}
                onUpdateTabData={updates => {
                  tabs.updateItem({ ...tab, ...updates });
                }}
                onCloneTab={handleCloneTab}
              />
            </AccordionGroup>
          ))}
        </TabPanels>
      </Tabs>
    </Stack>
  );
};
